import GuiContext from "config/context/guiContext";
import { useContext, useState } from "react";
import { flushSync } from "react-dom";
import { useReadComposition } from "./useReadComposition";
import { useGetCompositionScopesService } from "./useGetCompositionScopesService";
import { useCompositionAdvancedOptionsContext } from "../context/CompositionAdvancedOptionsContext";

/**
 * Brisanje redova koji odgovaraju uslovu — DELETE FROM ... WHERE.
 *
 * Dvokoračno: `preview` prebroji i ništa ne dira, `confirm` briše. Broj pred
 * očima je jedina zaštita koja išta vredi, jer lanac ne pamti prethodno stanje.
 *
 * Uslov je onaj koji je korisnik već postavio u filteru — ono što vidi na
 * ekranu je ono što nestaje. Prazan uslov backend odbija; za praznjenje cele
 * tabele postoji Clear.
 */
export const useDeleteWhere = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [matched, setMatched] = useState(null);
    const [done, setDone] = useState(null);

    const { readComposition } = useReadComposition();
    const { getCompositionScopes } = useGetCompositionScopesService();
    const {
        setPage, setNextKey, setPrevKey, setLocalReverse, setLocalLimit, limit,
    } = useCompositionAdvancedOptionsContext();
    const {
        selected: { selectedAccount, selectedServer },
        URL,
    } = useContext(GuiContext);

    const call = async (body) => {
        const res = await fetch(`${URL}/api/database/delete-where`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
                database: selectedAccount || "",
                server_url: selectedServer || "",
                permission: "active",
                ...body,
            }),
        });
        return res.json();
    };

    /** Koliko redova uslov pogađa. Ne dira ništa i ne traži ključ. */
    const preview = async ({ composition, scope, query, operator }) => {
        setLoading(true);
        setError(null);
        setDone(null);
        try {
            const data = await call({ composition, scope, query, operator, dry_run: true });
            if (data?.status) {
                setMatched(data.data?.matched ?? 0);
            } else {
                setError(data?.message || "Could not count matching rows");
                setMatched(null);
            }
            return data;
        } catch (err) {
            setError(err.message || "An error occurred");
            setMatched(null);
        } finally {
            setLoading(false);
        }
    };

    const confirm = async ({ composition, scope, query, operator, private_key }) => {
        setLoading(true);
        setError(null);
        try {
            const data = await call({ composition, scope, query, operator, private_key });
            if (data?.status) {
                setDone(data.data?.deleted ?? 0);
                setMatched(null);
                // Filter ostaje — korisnik i dalje gleda isti izbor. Resetuje se
                // samo straničenje, jer se broj redova promenio pod njim.
                flushSync(() => {
                    setNextKey("");
                    setPrevKey("");
                    setLocalReverse(false);
                    setLocalLimit(limit || 12);
                    setPage(1);
                });
                getCompositionScopes({ composition });
                readComposition({ composition });
            } else {
                setError(data?.message || "Delete failed");
            }
            return data;
        } catch (err) {
            setError(err.message || "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setLoading(false);
        setError(null);
        setMatched(null);
        setDone(null);
    };

    return { preview, confirm, reset, loading, error, matched, done };
};
